/**
 * Country-diff parent catalogue (ADR-0015 Phase A) — bilateral diff
 * metadata, language-invariant. Translatable prose
 * (`country_name`, `summary`, `details`) lives in
 * `./translations/{lang}/country-diffs.json`.
 *
 * IDs follow the convention `<lesson_id>__<country_code>` (e.g.
 * `traffic-signals__IN`). The current 12-lesson catalogue produces 60
 * country-diff records; future authoring can reuse a diff across
 * multiple lessons if the bilateral comparison applies to more than one
 * topic (the 1:M FK supports that, the import script just hasn't
 * exercised it yet).
 */
export type CountryDiffSeverity = "major" | "moderate" | "minor";

export interface LessonCountryDiffParent {
  readonly id: string;
  readonly lesson_id: string;
  readonly country_code: string;
  readonly flag: string;
  readonly severity: CountryDiffSeverity;
}

export const COUNTRY_DIFF_PARENTS: readonly LessonCountryDiffParent[] = [
  {
    id: "traffic-signals__IN",
    lesson_id: "traffic-signals",
    country_code: "IN",
    flag: "🇮🇳",
    severity: "major",
  },
  {
    id: "traffic-signals__GB",
    lesson_id: "traffic-signals",
    country_code: "GB",
    flag: "🇬🇧",
    severity: "moderate",
  },
  {
    id: "traffic-signals__DE",
    lesson_id: "traffic-signals",
    country_code: "DE",
    flag: "🇩🇪",
    severity: "moderate",
  },
  {
    id: "traffic-signals__MX",
    lesson_id: "traffic-signals",
    country_code: "MX",
    flag: "🇲🇽",
    severity: "minor",
  },
  {
    id: "traffic-signals__JP",
    lesson_id: "traffic-signals",
    country_code: "JP",
    flag: "🇯🇵",
    severity: "moderate",
  },
  {
    id: "road-signs__IN",
    lesson_id: "road-signs",
    country_code: "IN",
    flag: "🇮🇳",
    severity: "major",
  },
  {
    id: "road-signs__GB",
    lesson_id: "road-signs",
    country_code: "GB",
    flag: "🇬🇧",
    severity: "minor",
  },
  {
    id: "road-signs__DE",
    lesson_id: "road-signs",
    country_code: "DE",
    flag: "🇩🇪",
    severity: "minor",
  },
  {
    id: "speed-limits__IN",
    lesson_id: "speed-limits",
    country_code: "IN",
    flag: "🇮🇳",
    severity: "major",
  },
  {
    id: "speed-limits__DE",
    lesson_id: "speed-limits",
    country_code: "DE",
    flag: "🇩🇪",
    severity: "moderate",
  },
  {
    id: "speed-limits__GB",
    lesson_id: "speed-limits",
    country_code: "GB",
    flag: "🇬🇧",
    severity: "minor",
  },
  {
    id: "speed-limits__MX",
    lesson_id: "speed-limits",
    country_code: "MX",
    flag: "🇲🇽",
    severity: "major",
  },
  {
    id: "school-zones__IN",
    lesson_id: "school-zones",
    country_code: "IN",
    flag: "🇮🇳",
    severity: "major",
  },
  {
    id: "school-zones__GB",
    lesson_id: "school-zones",
    country_code: "GB",
    flag: "🇬🇧",
    severity: "minor",
  },
  {
    id: "intersections__IN",
    lesson_id: "intersections",
    country_code: "IN",
    flag: "🇮🇳",
    severity: "major",
  },
  {
    id: "intersections__GB",
    lesson_id: "intersections",
    country_code: "GB",
    flag: "🇬🇧",
    severity: "major",
  },
  {
    id: "intersections__DE",
    lesson_id: "intersections",
    country_code: "DE",
    flag: "🇩🇪",
    severity: "moderate",
  },
  {
    id: "four-way-stop__IN",
    lesson_id: "four-way-stop",
    country_code: "IN",
    flag: "🇮🇳",
    severity: "major",
  },
  {
    id: "four-way-stop__GB",
    lesson_id: "four-way-stop",
    country_code: "GB",
    flag: "🇬🇧",
    severity: "major",
  },
  {
    id: "four-way-stop__MX",
    lesson_id: "four-way-stop",
    country_code: "MX",
    flag: "🇲🇽",
    severity: "minor",
  },
  {
    id: "right-side-driving__GB",
    lesson_id: "right-side-driving",
    country_code: "GB",
    flag: "🇬🇧",
    severity: "major",
  },
  {
    id: "right-side-driving__IN",
    lesson_id: "right-side-driving",
    country_code: "IN",
    flag: "🇮🇳",
    severity: "major",
  },
  {
    id: "right-side-driving__AU",
    lesson_id: "right-side-driving",
    country_code: "AU",
    flag: "🇦🇺",
    severity: "major",
  },
  {
    id: "right-side-driving__JP",
    lesson_id: "right-side-driving",
    country_code: "JP",
    flag: "🇯🇵",
    severity: "major",
  },
  {
    id: "highway-driving__IN",
    lesson_id: "highway-driving",
    country_code: "IN",
    flag: "🇮🇳",
    severity: "major",
  },
  {
    id: "highway-driving__DE",
    lesson_id: "highway-driving",
    country_code: "DE",
    flag: "🇩🇪",
    severity: "minor",
  },
  {
    id: "highway-driving__GB",
    lesson_id: "highway-driving",
    country_code: "GB",
    flag: "🇬🇧",
    severity: "moderate",
  },
  {
    id: "dui-bac__IN",
    lesson_id: "dui-bac",
    country_code: "IN",
    flag: "🇮🇳",
    severity: "moderate",
  },
  {
    id: "dui-bac__DE",
    lesson_id: "dui-bac",
    country_code: "DE",
    flag: "🇩🇪",
    severity: "moderate",
  },
  {
    id: "dui-bac__GB",
    lesson_id: "dui-bac",
    country_code: "GB",
    flag: "🇬🇧",
    severity: "minor",
  },
  {
    id: "dui-bac__JP",
    lesson_id: "dui-bac",
    country_code: "JP",
    flag: "🇯🇵",
    severity: "major",
  },
  {
    id: "pedestrian-crosswalks__IN",
    lesson_id: "pedestrian-crosswalks",
    country_code: "IN",
    flag: "🇮🇳",
    severity: "major",
  },
  {
    id: "pedestrian-crosswalks__GB",
    lesson_id: "pedestrian-crosswalks",
    country_code: "GB",
    flag: "🇬🇧",
    severity: "minor",
  },
  {
    id: "pedestrian-crosswalks__MX",
    lesson_id: "pedestrian-crosswalks",
    country_code: "MX",
    flag: "🇲🇽",
    severity: "major",
  },
  {
    id: "parking-rules__IN",
    lesson_id: "parking-rules",
    country_code: "IN",
    flag: "🇮🇳",
    severity: "major",
  },
  {
    id: "parking-rules__GB",
    lesson_id: "parking-rules",
    country_code: "GB",
    flag: "🇬🇧",
    severity: "minor",
  },
  {
    id: "parking-rules__DE",
    lesson_id: "parking-rules",
    country_code: "DE",
    flag: "🇩🇪",
    severity: "moderate",
  },
  {
    id: "adverse-weather__IN",
    lesson_id: "adverse-weather",
    country_code: "IN",
    flag: "🇮🇳",
    severity: "major",
  },
  {
    id: "adverse-weather__GB",
    lesson_id: "adverse-weather",
    country_code: "GB",
    flag: "🇬🇧",
    severity: "minor",
  },
  {
    id: "adverse-weather__MX",
    lesson_id: "adverse-weather",
    country_code: "MX",
    flag: "🇲🇽",
    severity: "major",
  },
  {
    id: "adverse-weather__DE",
    lesson_id: "adverse-weather",
    country_code: "DE",
    flag: "🇩🇪",
    severity: "minor",
  },
] as const;
